<template>
  <b-sidebar
    id="drawer-approval-chain-division"
    :visible="visible"
    title="Atur Approval Chain"
    backdrop-variant="dark"
    backdrop
    right
    shadow
    width="520px"
    sidebar-class="right-drawer"
    aria-hidden="false"
    @hidden="onClose"
  >
    <div class="p-[1.5rem] flex flex-col justify-between min-h-[calc(100vh-80px)]">
      <div>
        <!-- Info Banner Alert -->
        <div class="flex items-start gap-[.75rem] p-[.875rem] rounded-2xl bg-[#F0F9FF] border-solid border-[1px] border-[#D5EFFF] mb-[1.25rem]">
          <span class="k-info-circle text-[20px] text-[#08A0F7] shrink-0 mt-[.125rem]" />
          <p class="text-sm text-[#475569] leading-relaxed mb-0">
            Atur urutan persetujuan lembur untuk divisi ini. Level terbawah akan menjadi pemberi persetujuan pertama.
          </p>
        </div>

        <!-- Division Name Subtitle -->
        <div class="mb-[1rem]">
          <span class="text-[#828282]">Divisi: {{ divisionData.division || divisionData.division_name }}</span>
          <div class="dashed-divider mt-[1rem]" />
        </div>

        <!-- Stepper / Chain Timeline -->
        <div class="relative">
          <!-- Dynamic Levels List -->
          <div
            v-for="(level, index) in levels"
            :key="level.id || index"
            class="relative flex items-start flex-wrap gap-[1rem] approval-level-item"
            :style="{ zIndex: activeDropdownLevel === index ? 100 : (levels.length - index + 5) }"
          >
            <div class="timeline-line" />
            <!-- Level Indicator Badge -->
            <div class="size-[42px] rounded-full border border-[#E2E8F0] bg-white text-[#F95031] text-[11px] font-semibold flex items-center justify-center shrink-0 z-10">
              Lvl {{ index + 1 }}
            </div>

            <!-- Level Card -->
            <div
              class="border rounded-2xl p-[1rem] bg-white flex-1"
              :style="{ zIndex: activeDropdownLevel === index ? 100 : 1 }"
            >
              <div class="flex items-center justify-between mb-[.5rem]">
                <span class="text-xs font-semibold text-black">
                  {{ index === 0 ? 'Approver Pertama' : `Approver Level ${index + 1}` }}
                </span>
                <button
                  v-if="levels.length > 1"
                  type="button"
                  class="text-[#DC2626] hover:text-[#B91C1C] border-0 bg-transparent cursor-pointer p-0 flex items-center focus:outline-none"
                  @click="handleRemoveLevel(index)"
                >
                  <span class="k-trash text-[16px] text-[#DC2626]" />
                </button>
              </div>

              <!-- Employee Select -->
              <v-select
                v-model="level.approver"
                :options="employeeOptions"
                :loading="isLoadingStaff"
                :filter-by="customFilterBy"
                label="name"
                placeholder="Pilih atau cari karyawan"
                class="custom-employee-select"
                @search="onSearchStaff"
                @open="activeDropdownLevel = index"
                @close="activeDropdownLevel = null"
              >
                <template #selected-option="option">
                  <div class="inline-flex items-center gap-[.5rem] py-[.125rem]">
                    <div>
                      <div class="text-sm font-medium text-black leading-tight">
                        {{ option.name }} <span
                          v-if="option.npk"
                          class="text-xs text-[#94A3B8] font-normal"
                        >({{ option.npk }})</span>
                      </div>
                      <div class="text-xs text-[#828282] leading-tight">
                        {{ option.role }}{{ option.division ? ` • ${option.division}` : '' }}
                      </div>
                    </div>
                  </div>
                </template>
                <template #option="option">
                  <div
                    class="flex items-center gap-[.5rem] py-[.25rem] hover:cursor-pointer hover:bg-slate-50 rounded-lg"
                  >
                    <div class="w-[28px] h-[28px] rounded-lg bg-[#F1F5F9] text-[#64748B] text-[10px] font-semibold flex items-center justify-center shrink-0">
                      {{ option.initials || (option.name ? option.name.substring(0, 2).toUpperCase() : '') }}
                    </div>
                    <div>
                      <div class="text-xs font-medium text-black">
                        {{ option.name }} <span
                          v-if="option.npk"
                          class="text-[11px] text-[#94A3B8] font-normal"
                        >({{ option.npk }})</span>
                      </div>
                      <div class="text-[11px] text-[#828282]">
                        {{ option.role }}{{ option.division ? ` • ${option.division}` : '' }}
                      </div>
                    </div>
                  </div>
                </template>
              </v-select>
            </div>

            <!-- Tambah Level Button -->
            <div
              v-if="index === levels.length - 1"
              class="pl-[58px] mb-[1.25rem] basis-full"
            >
              <BButton
                variant="outline-primary"
                class="!flex items-center gap-[.5rem] pill-dashed-button"
                size="sm"
                @click="handleAddLevel"
              >
                <span class="k-add scale-150" />
                <span>Tambah Level</span>
              </BButton>
            </div>
          </div>

          <!-- Approver Final (Otomatis) Card -->
          <div
            class="relative flex items-start gap-[1rem]"
            style="z-index: 1;"
          >
            <!-- Check Icon: patch-check-fill from vue bootstrap icon -->
            <div class="size-[42px] rounded-full flex items-center justify-center shrink-0 mt-[.25rem] bg-[#F95E16]">
              <BIcon
                icon="patch-check"
                class="text-primary bg-white rounded-full"
                font-scale="1.5"
              />
            </div>

            <!-- Card Box -->
            <div class="bg-[#FFF8F6] border-solid border-[1px] border-[#FFD0B5] rounded-2xl p-[1rem] flex-1">
              <div class="flex items-center gap-[.375rem] text-xs font-semibold text-black mb-[.5rem]">
                <feather-icon
                  icon="LockIcon"
                  class="w-3.5 h-3.5 text-[#828282]"
                />
                <span>Approver Final (Otomatis)</span>
              </div>
              <div
                v-if="hasFinalApprover"
                class="bg-white rounded-xl border-solid border-[1px] border-[#FFE8D9] p-[.75rem] flex items-center gap-[.75rem]"
              >
                <div class="w-[36px] h-[36px] rounded-lg bg-[#F1F5F9] text-[#64748B] text-xs font-semibold flex items-center justify-center shrink-0">
                  {{ finalApproverInitials }}
                </div>
                <div>
                  <div class="text-sm font-medium text-black leading-tight">
                    {{ finalApproverName }}
                  </div>
                  <div class="text-xs text-[#828282] leading-tight">
                    {{ finalApproverRole }}
                  </div>
                </div>
              </div>
              <div
                v-else
                class="bg-[#FFFBEB] rounded-xl border-solid border-[1px] border-[#FDE68A] p-[.75rem] flex items-center gap-[.75rem]"
              >
                <div class="w-[36px] h-[36px] rounded-lg bg-[#FEF3C7] text-[#D97706] text-xs font-semibold flex items-center justify-center shrink-0">
                  <feather-icon
                    icon="AlertCircleIcon"
                    class="w-4 h-4 text-[#D97706]"
                  />
                </div>
                <div>
                  <div class="text-xs font-semibold text-[#92400E] leading-tight">
                    Belum Ada Approver Final Global
                  </div>
                  <div class="text-[11px] text-[#B45309] leading-tight mt-0.5">
                    Silakan atur Approver Final Global terlebih dahulu pada tab "Approver Final (Global)".
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons Footer -->
      <div class="mt-[2rem]">
        <div
          v-if="divisionData && Number(divisionData.id) > 0"
          class="mb-[1rem] flex justify-center"
        >
          <BButton
            variant="link"
            size="sm"
            class="!text-[#DC2626] hover:!text-[#B91C1C] p-0 !flex items-center gap-[.35rem]"
            :disabled="isSubmitting"
            @click="handleDelete"
          >
            <span class="k-trash text-[16px] text-[#DC2626]" />
            <span class="text-xs font-medium">Hapus Konfigurasi Divisi Ini</span>
          </BButton>
        </div>
        <div class="flex items-between gap-[1rem]">
          <BButton
            size="md"
            variant="outline-primary"
            block
            :disabled="isSubmitting"
            @click="onClose"
          >
            Batalkan
          </BButton>
          <BButton
            size="md"
            variant="primary"
            block
            class="m-0 !flex items-center justify-center gap-1"
            :disabled="isSubmitting || levels.find(l => !l.approver) || !hasFinalApprover"
            @click="handleSave"
          >
            <BSpinner
              v-if="isSubmitting"
              small
              class="mr-1"
            />
            <span>Simpan Perubahan</span>
          </BButton>
        </div>
      </div>
    </div>
    <ModalConfirmation :modal="modalConfirm" />
  </b-sidebar>
</template>

<style scoped>
::v-deep .b-sidebar-header,
::v-deep .b-sidebar-body {
  background-color: white;
}

::v-deep .custom-employee-select .vs__dropdown-toggle {
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  min-height: 44px;
  padding: 2px 6px;
}

::v-deep .custom-employee-select .vs__selected {
  margin: 2px;
}

.timeline-line {
  position: absolute;
  left: 21px;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: #E2E8F0;
  z-index: 0;
}

.dashed-divider {
  height: 1px;
  background-image: repeating-linear-gradient(
    to right,
    #E2E2E2 0,
    #E2E2E2 6px,
    transparent 6px,
    transparent 12px
  );
}

::v-deep .custom-employee-select .vs__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-left: auto;
  padding: 0;
  height: 100%;
}

::v-deep .custom-employee-select .vs__dropdown-toggle {
  display: flex;
  align-items: center;
}

::v-deep .vs__search:focus {
  outline: none;
}

::v-deep .btn-outline-primary,
::v-deep .btn-primary {
  border-radius: 10px;
}

::v-deep .btn-outline-primary.pill-dashed-button {
  border-radius: 999px !important;
  border: 2px dashed #F95E16 !important;
}

.approval-level-item:focus-within {
  z-index: 100 !important;
}

.approval-level-item:has(.vs--open) {
  z-index: 100 !important;
}

::v-deep .custom-employee-select .vs__dropdown-menu {
  z-index: 1000 !important;
}
</style>

<script>
import vSelect from 'vue-select'
import { komtimAxiosIns } from '@/libs/axios'
import ModalConfirmation from '../../components/ModalConfirmation.vue'
import { confirmModalMessage } from '../../helpers/string-html'
import { searchApproverUsers, getGlobalFinalApprover } from '../config'
import { employeeOptions as defaultEmployeeOptions } from '../mockData'

export default {
  name: 'DrawerApprovalChainDivision',
  components: {
    vSelect,
    ModalConfirmation,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    divisionData: {
      type: Object,
      default: () => ({}),
    },
    globalApprover: {
      type: Object,
      default: () => null,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      employeeOptions: [],
      isLoadingStaff: false,
      searchTimer: null,
      activeDropdownLevel: null,
      levels: [{}],
      modalConfirm: {
        id: 'modal-confirm-remove-level-approver',
        title: '',
        description: '',
        negativeButtonAction: () => {},
        negativeButtonText: '',
        positiveButtonAction: () => {},
        positiveButtonText: '',
        closeButtonAction: () => {},
      },
      currentGlobalApprover: null,
    }
  },
  computed: {
    resolvedFinalApprover() {
      if (this.currentGlobalApprover && (this.currentGlobalApprover.user_id || this.currentGlobalApprover.id)) {
        return this.currentGlobalApprover
      }
      if (this.divisionData?.approval_final && typeof this.divisionData.approval_final === 'object') {
        const f = this.divisionData.approval_final
        if (f.user_id || f.id) {
          return {
            id: f.user_id || f.id,
            user_id: f.user_id || f.id,
            name: f.user_name || f.name || '',
            role: f.user_role || f.role || f.position_name || '',
          }
        }
      }
      if (this.globalApprover && (this.globalApprover.user_id || this.globalApprover.id)) {
        return this.globalApprover
      }
      return null
    },
    hasFinalApprover() {
      return !!(this.resolvedFinalApprover && (this.resolvedFinalApprover.user_id || this.resolvedFinalApprover.id))
    },
    finalApproverName() {
      if (this.resolvedFinalApprover) {
        return this.resolvedFinalApprover.name || this.resolvedFinalApprover.user_name || '-'
      }
      return 'Belum Diatur'
    },
    finalApproverRole() {
      if (this.resolvedFinalApprover) {
        return this.resolvedFinalApprover.role || this.resolvedFinalApprover.user_role || this.resolvedFinalApprover.position_name || '-'
      }
      return 'Atur Approver Final Global terlebih dahulu'
    },
    finalApproverInitials() {
      if (!this.resolvedFinalApprover) return '-'
      const name = this.finalApproverName
      if (!name || name === 'Belum Diatur') return '-'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
  },
  watch: {
    divisionData: {
      handler() {
        if (this.visible) {
          this.fetchCurrentFinalApprover()
          this.initLevels()
        }
      },
      deep: true,
    },
    visible(val) {
      if (val) {
        this.fetchStaffOptions()
        this.fetchCurrentFinalApprover()
        this.initLevels()
      }
    },
  },
  mounted() {
    this.fetchStaffOptions()
    this.fetchCurrentFinalApprover()
  },
  methods: {
    async fetchCurrentFinalApprover() {
      if (this.divisionData?.approval_final && typeof this.divisionData.approval_final === 'object') {
        const f = this.divisionData.approval_final
        if (f.user_id || f.id) {
          this.currentGlobalApprover = {
            id: f.user_id || f.id,
            user_id: f.user_id || f.id,
            user_name: f.user_name || f.name || '-',
            role: f.user_role || f.role || f.position_name || 'Finance',
          }
          return
        }
      }
      try {
        const finalApprover = await getGlobalFinalApprover()
        if (finalApprover && (finalApprover.user_id || finalApprover.id)) {
          this.currentGlobalApprover = finalApprover
        } else {
          this.currentGlobalApprover = null
        }
      } catch (e) {
        this.currentGlobalApprover = null
      }
    },
    initLevels() {
      const approversList = Array.isArray(this.divisionData?.approvers) && this.divisionData.approvers.length > 0
        ? this.divisionData.approvers
        : null

      if (approversList) {
        const nonFinalList = approversList.filter(appr => !appr.is_final)
        const finalAppr = approversList.find(appr => appr.is_final)
        if (finalAppr && (!this.currentGlobalApprover || !this.currentGlobalApprover.user_id)) {
          const uId = finalAppr.user_id || finalAppr.id
          const uName = finalAppr.user_name || finalAppr.name || finalAppr.full_name || ''
          const uRole = finalAppr.user_role || finalAppr.role || finalAppr.position_name || 'Finance'
          this.currentGlobalApprover = {
            id: uId,
            user_id: uId,
            user_name: uName,
            name: uName,
            role: uRole,
          }
        }

        if (nonFinalList.length > 0) {
          this.levels = nonFinalList.map((appr, idx) => {
            const userId = appr.user_id || appr.id
            const userName = appr.user_name || appr.name || appr.full_name || ''
            const userRole = appr.user_role || appr.role || appr.position_name || 'Approver'
            const found = this.employeeOptions.find(e => e.id === userId || e.name === userName)
            return {
              id: idx + 1,
              approver: found || {
                id: userId,
                user_id: userId,
                name: userName,
                full_name: userName,
                role: userRole,
                position_name: userRole,
                initials: (userName || '').substring(0, 2).toUpperCase(),
              },
            }
          })
        } else {
          this.levels = [{ id: 1, approver: null }]
        }
      } else if (this.divisionData && this.divisionData.approver1 && this.divisionData.approver1 !== '-') {
        const found = this.employeeOptions.find(
          e => e.id === this.divisionData.approver_1_id || e.name === this.divisionData.approver1,
        )
        this.levels = [
          {
            id: 1,
            approver: found || {
              id: this.divisionData.approver_1_id || null,
              user_id: this.divisionData.approver_1_id || null,
              name: this.divisionData.approver1,
              role: this.divisionData.approver1Role || 'Approver 1',
            },
          },
        ]
      } else {
        this.levels = [{ id: 1, approver: null }]
      }
    },
    onClose() {
      this.$emit('on-close')
    },
    handleAddLevel() {
      const newId = this.levels.length + 1
      this.levels.push({
        id: newId,
        approver: null,
      })
    },
    handleRemoveLevel(index) {
      if (this.levels.length <= 1) return

      const levelNumber = index + 1
      const approverName = this.levels[index]?.approver?.name

      this.modalConfirm.title = `Hapus Approver Level ${levelNumber}?`
      this.modalConfirm.description = confirmModalMessage(
        approverName
          ? `Approver <strong>${approverName}</strong> pada Level ${levelNumber} akan dihapus dari chain divisi ini. Penghapusan ini hanya berlaku untuk pengajuan baru dan <strong>tidak memengaruhi pengajuan lama</strong> yang sedang berjalan. Apakah Anda ingin melanjutkan?`
          : `Approver pada Level ${levelNumber} akan dihapus dari chain divisi ini. Penghapusan ini hanya berlaku untuk pengajuan baru dan <strong>tidak memengaruhi pengajuan lama</strong> yang sedang berjalan. Apakah Anda ingin melanjutkan?`,
      )
      this.modalConfirm.negativeButtonText = 'Batalkan'
      this.modalConfirm.negativeButtonAction = () => {
        this.$bvModal.hide(this.modalConfirm.id)
      }
      this.modalConfirm.positiveButtonText = 'Ya, Hapus'
      this.modalConfirm.positiveButtonAction = () => {
        this.levels.splice(index, 1)
        this.$bvModal.hide(this.modalConfirm.id)
      }
      this.modalConfirm.closeButtonAction = () => {
        this.$bvModal.hide(this.modalConfirm.id)
      }

      this.$bvModal.show(this.modalConfirm.id)
    },
    handleSave() {
      const finalApproverId = this.currentGlobalApprover?.user_id
        || this.currentGlobalApprover?.id
        || this.divisionData?.approver_final_id
        || this.divisionData?.approval_final?.user_id
        || this.divisionData?.approval_final?.id

      const divisionId = Number(
        this.divisionData?.division_id
        || this.divisionData?.raw?.division_id
        || this.divisionData?.id,
      )

      this.$emit('on-save', {
        divisionData: this.divisionData,
        divisionId,
        levels: this.levels,
        finalApprover: this.currentGlobalApprover,
        finalApproverId,
      })
    },
    handleDelete() {
      this.$emit('on-delete', this.divisionData)
    },
    onSearchStaff(search) {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.fetchStaffOptions(search || '')
      }, 400)
    },
    customFilterBy(option, label, search) {
      const q = (search || '').toLowerCase().trim()
      if (!q) return true
      const name = String(option.name || option.full_name || '').toLowerCase()
      const npk = String(option.npk || '').toLowerCase()
      const role = String(option.role || option.position_name || '').toLowerCase()
      const division = String(option.division || option.division_name || '').toLowerCase()
      return name.includes(q) || npk.includes(q) || role.includes(q) || division.includes(q)
    },
    async fetchStaffOptions(keyword = '') {
      this.isLoadingStaff = true
      try {
        const list = await searchApproverUsers(keyword)
        if (Array.isArray(list)) {
          const map = new Map()
          this.levels.forEach(lvl => {
            if (lvl.approver && (lvl.approver.id || lvl.approver.user_id)) {
              const id = lvl.approver.id || lvl.approver.user_id
              map.set(id, lvl.approver)
            }
          })
          list.forEach(item => map.set(item.id, item))
          this.employeeOptions = Array.from(map.values())

          this.levels = this.levels.map(lvl => {
            if (lvl.approver && (lvl.approver.id || lvl.approver.user_id)) {
              const apprId = lvl.approver.id || lvl.approver.user_id
              const found = this.employeeOptions.find(e => e.id === apprId)
              if (found) {
                return {
                  ...lvl,
                  approver: found,
                }
              }
            }
            return lvl
          })
        }
      } catch (error) {
        // Fallback
      } finally {
        this.isLoadingStaff = false
      }
    },
  },
}
</script>
